import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { Document } from "../../../Models/knowledge";
import { ProcessingStatus } from "../../../Enums/companyStatus";
import TempUploadedFilesPanel from "./TempUploadedFilesPanel";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { isPreviewableFile } from "../../../Helpers/documentHelper";
import ArticleIcon from "@mui/icons-material/Article";

interface DocumentListProps {
  documentsData: Document[];
  isDownloading: boolean;
  onDownloadDocument: (id: number, name: string) => void;
  selectedIds?: number[];
  onToggleSelect?: (id: number) => void;
  onDeleteDocument?: (id: number) => void;
  isDeleting?: boolean;
  onPreviewDocument: (documentId: number, fileName: string) => void;
  onViewLogs: (documentId: number) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  documentsData,
  isDownloading,
  onDownloadDocument,
  selectedIds = [],
  onToggleSelect,
  onDeleteDocument,
  isDeleting = false,
  onPreviewDocument,
  onViewLogs,
}) => {
  const { t } = useTranslation("documentUpload");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {documentsData &&
        documentsData.map((file) => {
          const isError = file.status === ProcessingStatus.ERROR;
          const isProcessing = file.status === ProcessingStatus.IN_PROGRESS;
          const isSelected = selectedIds.includes(file.id);

          return (
            <Box
              key={file.id}
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: color.white,
                borderRadius: "6px",
                border: "1px solid #e0e0e0",
                padding: "12px",
                transition: "all 0.2s",
                position: "relative",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.03)",
                  borderColor: color.accent,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {onToggleSelect && !isProcessing && (
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => onToggleSelect(file.id)}
                  />
                )}

                {isProcessing ? (
                  <CircularProgress size={18} sx={{ color: "orange" }} />
                ) : (
                  <Box sx={{ opacity: isError ? 0.5 : 1 }}>
                    <TempUploadedFilesPanel
                      file={file}
                      getFileName={(file) => file.name}
                      getFileStatus={(file) => file.status}
                      getFileCreated={(file) => file.created_at}
                      getFileUpdated={(file) => file.updated_at}
                    />
                  </Box>
                )}
              </Box>
              {isError && (
                <CloseIcon
                  sx={{
                    position: "absolute",
                    left: 10,
                    color: "red",
                    opacity: 0.7,
                  }}
                />
              )}
              {!isProcessing && (
                <Box sx={{ display: "flex", gap: "4px", ml: "auto" }}>
                  {onPreviewDocument &&
                    file?.name &&
                    isPreviewableFile(file.name) && (
                      <Tooltip title={t("documentUpload.preview")}>
                        <span>
                          <IconButton
                            size="small"
                            sx={{ color: color.accent }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onPreviewDocument(file.id, file.name);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  <Tooltip title={t("documentUpload.viewLogs")}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ color: "orange" }}
                        onClick={() => onViewLogs(file.id)}
                      >
                        <ArticleIcon
                          sx={{ fontSize: "16px", color: color.accent }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("documentUpload.downloadDocument")}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ color: "orange" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDownloadDocument(file.id, file.name);
                        }}
                        disabled={isDownloading || isError}
                      >
                        <CloudDownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {onDeleteDocument && (
                    <Tooltip title={t("documentUpload.deleteDocument")}>
                      <span>
                        <IconButton
                          size="small"
                          sx={{ color: "red" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDocument(file.id);
                          }}
                          disabled={isDeleting}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
    </Box>
  );
};

export default DocumentList;
